import { Component, OnInit, Inject, ViewChild, ElementRef, DestroyRef, inject, signal, AfterViewInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountService } from 'src/app/services/account/account.service';
import { PrivacyPolicyComponent } from '../info/privacy-policy/privacy-policy.component';
import { EulaComponent } from '../info/eula/eula.component';
import { FormGroup, FormBuilder, Validators, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { skip } from 'rxjs/operators';
import device from 'current-device';
import { SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { UserProfile } from 'src/app/models/user-profile';
import { HttpService } from 'src/app/services/core/http.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { EvLoaderComponent } from '../../ui-components/ev-loader/ev-loader.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { SignInStepComponent } from './sign-in-step/sign-in-step.component';
import { RegisterCredentialsStepComponent } from './register-credentials-step/register-credentials-step.component';
import { ProfileStepComponent } from './profile-step/profile-step.component';
import { VerificationPendingStepComponent } from './verification-pending-step/verification-pending-step.component';
import { VerificationResultStepComponent } from './verification-result-step/verification-result-step.component';
import { ChangePasswordStepComponent } from './change-password-step/change-password-step.component';
import { ForgotPasswordStepComponent } from './forgot-password-step/forgot-password-step.component';

@Component({
  standalone: true,
  selector: 'app-account-dialog',
  templateUrl: './account-dialog.component.html',
  imports: [
    CommonModule, ReactiveFormsModule, EvLoaderComponent,
    SignInStepComponent, RegisterCredentialsStepComponent, ProfileStepComponent,
    VerificationPendingStepComponent, VerificationResultStepComponent,
    ChangePasswordStepComponent, ForgotPasswordStepComponent
  ],
  styleUrls: ['./account-dialog.component.scss']
})
export class AccountDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('accountScrollbar', { static: true }) accountScrollbar!: ElementRef<HTMLElement>;
  @ViewChild('profileStep') profileStep!: ProfileStepComponent;
  /**
   * Every value the template renders is a signal: HTTP responses are delivered outside the
   * Angular zone, so a signal write is what notifies change detection that they changed.
   */
  public readonly activeStepIndex = signal<number>(0);
  public readonly registerError = signal<boolean>(false);
  public readonly existingEmailMsg = signal<string | null>(null);
  public readonly registerLoader = signal<boolean>(false);
  public readonly accountDialogTitles = signal<string[]>([]);
  public readonly firstStepError = signal<string>("");
  public readonly countryList = signal<{ name: string, dial_code: string, code: string }[]>([]);
  public readonly backendErrorMsg = signal<string | null>(null);
  public readonly emailSent = signal<boolean>(false);
  public readonly invalidActualPassword = signal<boolean>(false);
  public readonly verifyFailed = signal<boolean>(false);
  public readonly passwordChangeSuccess = signal<boolean>(false);
  public readonly preiOS13WebView = signal<boolean>(false);
  public readonly androidWebView = signal<boolean>(false);

  /** Internal only — never rendered, so it stays a plain field. */
  public resendEmailAddress: string | null = null;

  /* form groups */
  public firstStepFormGroup!: FormGroup;
  public secondStepFormGroup!: FormGroup;
  public thirdStepFormGroup!: FormGroup;
  public sixthStepFormGroup!: FormGroup;
  public seventhStepFormGroup!: FormGroup;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<AccountDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, public accountService: AccountService,
    private readonly fb: FormBuilder, private readonly stackedModalsService: StackedModalsService,
    public webviewService: WebviewService, public settingsService: SettingsService,
    public languageService: LanguageService, private readonly httpService: HttpService, private readonly utilsService: UtilsService) {
  }

  /* set variables, load countrylist */
  ngOnInit() {
    this.accountDialogTitles.set([
      this.languageService.languageJSON.Global_Account,
      this.languageService.languageJSON.AccountDialog_Registration,
      this.languageService.languageJSON.AccountDialog_Registration,
      this.languageService.languageJSON.AccountDialog_Registration,
      this.languageService.languageJSON.AccountDialog_Registration,
      this.languageService.languageJSON.Global_Account,
      this.languageService.languageJSON.AccountDialog_ChangePassword,
      this.languageService.languageJSON.AccountDialog_ForgotPassword
    ]);

    this.firstStepFormGroup = this.fb.group({
      loginEmail: ['', [
      ]],
      loginPassword: ['', [
      ]],
      rememberMe: ['', [
      ]]
    });

    this.firstStepFormGroup.valueChanges.subscribe(val => {
      this.firstStepError.set("");
    });

    this.secondStepFormGroup = this.fb.group({
      registerEmail: ['', [
        Validators.required,
        Validators.email
      ]],
      registerPassword: ['', [
        Validators.required,
        Validators.pattern(/^(?=.*\d)(?=.*[A-Za-z])(?!.*\s).{8,200}$/)
      ]],
      registerPasswordConfirmation: ['', [
        Validators.required,
        Validators.pattern(/^(?=.*\d)(?=.*[A-Za-z])(?!.*\s).{8,200}$/)
      ]],
      registerNewsletter: ['', [
      ]]
    }, { validators: this.checkPasswords });

    this.thirdStepFormGroup = this.fb.group({
      registerFirstName: ['', [
        Validators.required,
        Validators.pattern(/^.{2,64}$/)
      ]],
      registerLastName: ['', [
        Validators.required,
        Validators.pattern(/^.{2,64}$/)
      ]],
      registerCountry: ['', [
        Validators.pattern(/^[a-zA-Z ]{2}$/)
      ]],
      registerPhone: ['', [
        Validators.pattern(/^[-0-9+/ ()]*$/)
      ]]
    });

    this.sixthStepFormGroup = this.fb.group({
      actualPassword: ['', [
        Validators.required
      ]],
      newPassword: ['', [
        Validators.required,
        Validators.pattern(/^(?=.*\d)(?=.*[A-Za-z])(?!.*\s).{8,200}$/)
      ]],
      newPasswordConfirmation: ['', [
        Validators.required,
        Validators.pattern(/^(?=.*\d)(?=.*[A-Za-z])(?!.*\s).{8,200}$/)
      ]]
    }, { validators: this.checkNewPasswords });

    this.seventhStepFormGroup = this.fb.group({
      recoveryEmail: ['', [
        Validators.required,
        Validators.email
      ]]
    })

    this.httpService.getCountryListJSON("en").subscribe((resp: any) => {
      this.countryList.set(resp);
    });

    this.activeStepIndex.set(0);
    if (this.data.process == "register") {
      this.activeStepIndex.set(1);
    }
    if (this.data.process == "profileEdit") {
      this.initEditProfile();
    }
    if (this.data.process == "verifySuccess") {
      this.activeStepIndex.set(4);
    }
    if (this.data.process == "verifyFailed") {
      this.activeStepIndex.set(4);
      this.verifyFailed.set(true);
    }

    this.accountService.ObservableSocialLoginResponse.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe((loginResponse) => {
      if (loginResponse) {
        if (loginResponse.AuthResponse) {
          this.close();
        }
        if (loginResponse.ErrorMessage) {
          this.stackedModalsService.openModalWindow(ConfirmationDialogComponent,
            {
              data: {
                title: this.languageService.languageJSON.Global_Error,
                message: loginResponse.ErrorMessage,
                buttons: [this.languageService.languageJSON.Global_Ok, null]
              }, autoFocus: false
            }).afterClosed().subscribe((resp) => { this.accountService.logout() });
        }
      }
    });

    this.preiOS13WebView.set(this.webviewService.IsGPSTWebview() && device.ios() && !device.desktop() &&
      (navigator.userAgent.includes('OS 12') || navigator.userAgent.includes('OS 11') ||
        navigator.userAgent.includes('OS 10') || navigator.userAgent.includes('OS 9') ||
        navigator.userAgent.includes('OS 8') || navigator.userAgent.includes('OS 7')));

    this.androidWebView.set(this.webviewService.IsGPSTWebview() && device.android());
  }

  ngAfterViewInit(): void {
    if (this.activeStepIndex() == 5) {
      this.initAvatar();
    }
  }

  private initEditProfile(): void {
    this.activeStepIndex.set(5);
    const userProfile = this.accountService.getUserProfile();

    this.thirdStepFormGroup.addControl('registerNewsletter', new FormControl());
    this.thirdStepFormGroup.setValue({
      registerFirstName: userProfile.Profile.FirstName ? userProfile.Profile.FirstName : "",
      registerLastName: userProfile.Profile.LastName ? userProfile.Profile.LastName : "",
      registerCountry: userProfile.Profile.Country ? userProfile.Profile.Country : "",
      registerPhone: userProfile.Profile.Phone ? userProfile.Profile.Phone : "",
      registerNewsletter: userProfile.Profile.Newsletter ? userProfile.Profile.Newsletter : ""
    });

    this.registerFirstName.markAllAsTouched();
    this.registerLastName.markAllAsTouched();
  }

  private checkPasswords(group: FormGroup): { notSame: boolean } | null {
    let pass = group.get('registerPassword')?.value;
    let confirmPass = group.get('registerPasswordConfirmation')?.value;

    return pass === confirmPass ? null : { notSame: true }
  }

  private checkNewPasswords(group: FormGroup): { notSame: boolean } | null {
    let pass = group.get('newPassword')?.value;
    let confirmPass = group.get('newPasswordConfirmation')?.value;

    return pass === confirmPass ? null : { notSame: true }
  }

  get loginEmail() {
    return this.firstStepFormGroup.get("loginEmail")!;
  }

  get loginPassword() {
    return this.firstStepFormGroup.get("loginPassword")!;
  }

  get rememberMe() {
    return this.firstStepFormGroup.get("rememberMe")!;
  }

  get registerEmail() {
    return this.secondStepFormGroup.get("registerEmail")!;
  }

  get registerPassword() {
    return this.secondStepFormGroup.get("registerPassword")!;
  }

  get registerPasswordConfirmation() {
    return this.secondStepFormGroup.get("registerPasswordConfirmation")!;
  }

  get registerPasswordsMatch() {
    return this.secondStepFormGroup.hasError('notSame');
  }

  get registerNewsletter() {
    return this.secondStepFormGroup.get("registerNewsletter")!;
  }

  get registerFirstName() {
    return this.thirdStepFormGroup.get("registerFirstName")!;
  }

  get registerLastName() {
    return this.thirdStepFormGroup.get("registerLastName")!;
  }

  get registerCountry() {
    return this.thirdStepFormGroup.get("registerCountry")!;
  }

  get registerPhone() {
    return this.thirdStepFormGroup.get("registerPhone")!;
  }

  get actualPassword() {
    return this.sixthStepFormGroup.get("actualPassword")!;
  }

  get newPassword() {
    return this.sixthStepFormGroup.get("newPassword")!;
  }

  get newPasswordConfirmation() {
    return this.sixthStepFormGroup.get("newPasswordConfirmation")!;
  }

  get newPasswordsMatch() {
    return this.sixthStepFormGroup.hasError('notSame');
  }

  get recoveryEmail() {
    return this.seventhStepFormGroup.get("recoveryEmail")!;
  }

  get secondFormGroupValid() {
    return this.secondStepFormGroup.valid;
  }

  get thirdFormGroupValid() {
    return this.thirdStepFormGroup.valid;
  }

  get sixthFormGroupValid() {
    return this.sixthStepFormGroup.valid;
  }

  get seventhFormGroupValid() {
    return this.seventhStepFormGroup.valid;
  }

  private async initAvatar(): Promise<void> {
    this.profileStep?.initAvatar();
  }

  public onAvatarProcessing(processing: boolean) {
    this.registerLoader.set(processing);
  }

  /* signing in a user */
  public signIn(): void {
    const login = {
      email: this.loginEmail.value,
      password: this.loginPassword.value,
      rememberMe: this.rememberMe.value
    };
    this.registerLoader.set(true);
    this.accountService.login(login).subscribe({
      next: (resp) => {
        this.registerLoader.set(false);
        if (resp.failed && resp.failed == "USER_ERR_205") {
          this.resendEmailAddress = this.loginEmail.value;
          this.activeStepIndex.set(3);
        }
        else {
          this.dialogRef.close();
        }
      },
      error: (error) => {
        this.registerLoader.set(false);
        if (error?.name === "TimeoutError") {
          this.firstStepError.set(this.languageService.languageJSON.Global_SomethingWentWrong);
        }
        else {
          this.firstStepError.set(this.languageService.languageJSON.AccountDialog_SignIn_Error);
        }
      }
    });
  }

  /**
   * take to the next step of the form
   * validate user inputs
   */
  public nextStep(): void {
    if (!this.registerLoader()) {
      if ((this.activeStepIndex() == 1 && !this.secondFormGroupValid)) {
        this.secondStepFormGroup.markAllAsTouched();
      }
      else if (this.activeStepIndex() == 2 && !this.thirdFormGroupValid) {
        this.thirdStepFormGroup.markAllAsTouched();
      }
      else {
        if (this.activeStepIndex() == 2) {
          this.registerUser();
        }
        else if (this.activeStepIndex() == 1) {
          this.registerLoader.set(true);
          this.registerError.set(false);
          this.accountService.isExist(this.registerEmail.value).subscribe({
            next: (resp) => {
              this.registerLoader.set(false);
              if (resp.success?.message == "USER_MSG_001") {
                this.existingEmailMsg.set(this.registerEmail.value + " " + this.languageService.languageJSON.AccountDialog_Email_Taken);
              }
              if (resp.failed?.error == "USER_ERR_201") {
                this.existingEmailMsg.set(null);
                this.activeStepIndex.update(index => index + 1);
                this.accountService.getCountry().subscribe((resp) => {
                  this.registerCountry.setValue(resp.success);
                })
              }
            },
            error: (error) => {
              this.registerLoader.set(false);
              this.registerError.set(true);
            }
          });
        }
        else {
          this.activeStepIndex.update(index => index + 1);
        }
        this.scrollDialogToTop();
      }
    }
  }

  /* go back to the form previous step */
  public goBack(): void {
    if (!this.registerLoader()) {
      this.activeStepIndex.update(index => index - 1);
      this.backendErrorMsg.set(null);
      if (this.activeStepIndex() == 5) {
        this.initAvatar();
      }
    }
  }

  /* register a user */
  public registerUser(): void {
    let profile = new UserProfile;
    profile = {
      Email: this.registerEmail.value,
      FirstName: this.registerFirstName.value,
      LastName: this.registerLastName.value,
      Country: this.registerCountry.value,
      Phone: this.registerPhone.value,
      Avatar: this.profileStep?.getAvatarFile()!,
      Newsletter: this.registerNewsletter.value
    };

    this.registerLoader.set(true);
    this.registerError.set(false);
    this.accountService.registerUser(profile, this.registerPassword.value).subscribe({
      next: (resp) => {
        this.registerLoader.set(false);
        if (resp?.success) {
          this.resendEmailAddress = this.registerEmail.value;
          this.activeStepIndex.update(index => index + 1);
        }
      },
      error: (error) => {
        this.registerLoader.set(false);
        if (error?.name === "TimeoutError") {
          this.registerError.set(true);
        }
        if (error?.failed) {
          this.backendErrorMsg.set(error.failed.error);
        }
      }
    });
  }

  /* sending a new email with the verification code */
  public resendEmail(): void {
    this.registerError.set(false);
    this.registerLoader.set(true);
    this.accountService.resendActivationEmail(this.resendEmailAddress!).subscribe({
      next: (resp) => {
        this.registerLoader.set(false);
        if (resp.success) {
          this.emailSent.set(true);
        }
      },
      error: (error) => {
        this.registerLoader.set(false);
        this.registerError.set(true);
      }
    });
  }

  /* save a modified profile */
  public saveProfile(): void {
    this.thirdStepFormGroup.markAllAsTouched();
    this.registerError.set(false);
    this.backendErrorMsg.set(null);
    if (this.thirdFormGroupValid) {
      let profile = new UserProfile;
      profile = {
        Email: this.registerEmail.value,
        FirstName: this.registerFirstName.value,
        LastName: this.registerLastName.value,
        Country: this.registerCountry.value || "",
        Phone: this.registerPhone.value,
        Avatar: this.profileStep?.getAvatarFile()!,
        Newsletter: this.thirdStepFormGroup.get("registerNewsletter")?.value
      };

      this.registerLoader.set(true);
      this.accountService.setUser(profile).subscribe({
        next: (resp) => {
          this.registerLoader.set(false);
          if (resp?.success) {
            this.dialogRef.close();
          }
        },
        error: (error) => {
          this.registerLoader.set(false);
          if (error?.name === "TimeoutError") {
            this.registerError.set(true);
          }
          else if (error?.error?.error?.avatar) {
            this.backendErrorMsg.set(error.error.error.avatar);
          }
          else if (error?.status == 406) {
            this.backendErrorMsg.set(error.error.error);
          }
          else if (error?.status == 401) {
            this.dialogRef.close();
          }
        }
      });
    }
  }

  public deleteProfile(): void {
    // delete data global warning message
    let deleteData: { title: string, message: string, warning: string, buttons: string[] } = {
      title: this.languageService.languageJSON.AccountDialog_DeleteAccount,
      message: this.languageService.languageJSON.AccountDialog_DeleteAccount_Message,
      warning: this.languageService.languageJSON.AccountDialog_DeleteAccount_Warning,
      buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
    };

    let dialogRef = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, { data: deleteData, panelClass: 'delete-confirmation', autoFocus: false });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        setTimeout(() => {
          this.deleteConfirmationHandler();
        }, 100);
      }
    });
  }

  private deleteConfirmationHandler(): void {
    this.registerLoader.set(true);
    this.registerError.set(false);
    this.accountService.deleteUser().subscribe({
      next: (result) => {
        this.registerLoader.set(false);
        if (result?.failed) {
          this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
            data: {
              title: this.languageService.languageJSON.AccountDialog_DeleteAccount,
              message: this.languageService.languageJSON[result.failed.error],
              buttons: [this.languageService.languageJSON.Global_Ok, null]
            }, autoFocus: false
          });
        }
        else {
          this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
            data: {
              title: this.languageService.languageJSON.AccountDialog_DeleteAccount,
              message: this.languageService.languageJSON.AccountDialog_DeleteAccount_Confirm,
              buttons: [this.languageService.languageJSON.Global_Ok, null]
            }, autoFocus: false
          });
          this.accountService.logout();
          this.dialogRef.close();
        }
      },
      error: (error) => {
        this.registerLoader.set(false);
        if (error?.name === "TimeoutError") {
          this.registerError.set(true);
        }
        else if (error?.status == 401) {
          this.dialogRef.close();
        }
      }
    });
  }

  public changePassword(): void {
    if (this.passwordChangeSuccess()) {
      this.dialogRef.close();
    }
    else if (this.sixthFormGroupValid) {
      const email = this.accountService.getUserProfile().Profile.Email;
      this.registerLoader.set(true);
      this.registerError.set(false);
      this.accountService.changePassword(email, this.actualPassword.value, this.newPassword.value, this.newPassword.value).subscribe({
        next: (resp) => {
          this.registerLoader.set(false);
          if (resp.success) {
            this.invalidActualPassword.set(false);
            this.passwordChangeSuccess.set(true);
          }
        },
        error: (error) => {
          this.registerLoader.set(false);

          if (error?.status == 401) {
            if (error.error.failed?.error == "USER_ERR_202") {
              this.invalidActualPassword.set(true);
            }
            else {
              this.dialogRef.close();
            }
          }
          if (error?.name === "TimeoutError") {
            this.registerError.set(true);
          }
        }
      });
    }
    else {
      this.sixthStepFormGroup.markAllAsTouched();
    }
  }

  public goBackToLogin(): void {
    this.activeStepIndex.set(0);
  }

  public recoverPassword(): void {
    this.existingEmailMsg.set(null);
    if (this.seventhFormGroupValid) {
      this.registerLoader.set(true);
      this.accountService.getForgottenPassword(this.recoveryEmail.value).subscribe({
        next: (resp) => {
          this.registerLoader.set(false);
          if (resp?.status === true) {
            this.emailSent.set(true);
            this.existingEmailMsg.set(null);
          }
        },
        error: (error) => {
          this.registerLoader.set(false);
          if (error?.name === "TimeoutError") {
            this.registerError.set(true);
          }
          if (error?.error?.message === "USER_ERR_206") {
            this.existingEmailMsg.set(this.recoveryEmail.value + " " + this.languageService.languageJSON.AccountDialog_ForgotPassword_Error);
          }
        }
      });
    }
    else {
      this.seventhStepFormGroup.markAllAsTouched();
    }
  }

  public openForgotPassword(): void {
    this.activeStepIndex.set(7);
    this.emailSent.set(false);
    this.scrollDialogToTop();
  }

  private scrollDialogToTop(): void {
    this.accountScrollbar?.nativeElement?.scrollTo({ top: 0, behavior: 'auto' });
  }

  /* open privacy policy in a modal window */
  public openPrivacyPolicy(): void {
    this.stackedModalsService.openModalWindow(PrivacyPolicyComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public openTermsOfUse(): void {
    this.stackedModalsService.openModalWindow(EulaComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public openEUDataActPolicy(): void {
    this.utilsService.openUrlOnNewTab(device, "./assets/eu-data-policy.html",);
  }

  public downloadUserData(): void {
    this.httpService.getExportPersonalData().subscribe((resp: any) => {
      if (resp) {
        if (this.webviewService.IsGPSTWebview()) {
          this.webviewService.DownloadFile(resp, "user-data-export.csv");
        }
        else {
          this.utilsService.downloadCSVFile(resp, "user-data-export");
        }
      }
    });
  }

  /* close dialog */
  public close(): void {
    if (this.data.process == "verifySuccess") {
      globalThis.history.pushState("", "", '/');
    }
    this.dialogRef.close();
  }
}
